/** Words that keep their capitals when a code is turned into a sentence. */
const ACRONYMS: ReadonlySet<string> = new Set([
  'ach',
  'ai',
  'api',
  'csr',
  'csv',
  'gp',
  'hvac',
  'iaq',
  'id',
  'pdf',
  'sku',
  'sla',
  'sms',
  'url',
]);

/**
 * Upper-cases the first letter and leaves the rest alone.
 *
 * @param text any text
 * @returns the text with a capital first letter
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Turns a code into a sentence-case phrase: `BELOW_GP_FLOOR` becomes "Below GP floor".
 *
 * This is the fallback for every code the vocabulary has no wording for, so a value the server
 * adds before the interface learns it still reads as words. Text that already has a space in it
 * is somebody's own wording rather than a code and is returned untouched, which keeps a stored
 * free-text value such as "Google Ads" from being flattened to "Google ads".
 *
 * @param code an enum value, an identifier or a camel-case name
 * @returns the phrase, or an empty string for an empty code
 */
export function humanize(code: string): string {
  const text = code.trim();
  if (text === '' || /\s/.test(text)) {
    return text;
  }
  return text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[_\-\s]+/)
    .filter((word) => word !== '')
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) {
        return lower.toUpperCase();
      }
      return index === 0 ? capitalize(lower) : lower;
    })
    .join(' ');
}

/**
 * Reads a code's wording from a map, humanizing any code the map does not know.
 *
 * @param labels the vocabulary for one enum
 * @param code the value to name; a missing value has no wording
 * @returns the label, or an empty string when there is no code
 */
export function labelOf<K extends string>(
  labels: Readonly<Record<K, string>>,
  code: string | null | undefined,
): string {
  if (code === null || code === undefined || code === '') {
    return '';
  }
  const known: Readonly<Record<string, string>> = labels;
  // hasOwn, not a bare lookup: a code such as "constructor" must not read the prototype.
  return (Object.hasOwn(known, code) ? known[code] : undefined) ?? humanize(code);
}

/**
 * The values a vocabulary map covers, in the order it declares them.
 *
 * @param labels the vocabulary for one enum
 * @returns its keys
 */
export function keysOf<K extends string>(labels: Readonly<Record<K, unknown>>): K[] {
  return Object.keys(labels) as K[];
}

/**
 * The map's entries as `{ value, label }` pairs, for building a select.
 *
 * @param labels the vocabulary for one enum
 * @returns one option per value, in declaration order
 */
export function optionsOf<K extends string>(
  labels: Readonly<Record<K, string>>,
): { readonly value: K; readonly label: string }[] {
  return keysOf(labels).map((value) => ({ value, label: labels[value] }));
}
